import { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/state/store';
import { toggleDebugMenu, setCurrentlyViewingReward, setRewardClaimError, setCurrentlyViewingMedia } from '@/state/features/app';
import Modal from '@/components/Modal';
import { useTheme } from '@/theme';
import * as arise from '@/arise/api';
import { clearProfileV2FromLocalStorage } from '@/utils/localProfileV2';
import { clearLocalVideoProgressFromStorage } from '@/utils/localVideoProgress';
import { clearSavedLangIso } from '@/utils/localLang';
import { appVersion } from '@/utils/version';
import LanguageCycler from './LanguageCycler';

import * as styles from './styles.module.scss';

function TwitchDebugPanel() {
    const [logs, setLogs] = useState<string[]>([]);
    const [proxyOn, setProxyOn] = useState(() => {
        try {
            return window.localStorage.getItem('ARISE_TWITCH_PROXY') === '1';
        } catch {
            return false;
        }
    });
    const refresh = () => setLogs([...((window as any).__debugLogs || [])]);
    const copy = () => {
        const text = ((window as any).__debugLogs || []).join('\n');
        navigator.clipboard?.writeText(text).catch(() => {});
    };
    const clear = () => {
        (window as any).__debugLogs = [];
        setLogs([]);
    };
    const toggleProxy = () => {
        const next = !proxyOn;
        try {
            window.localStorage.setItem('ARISE_TWITCH_PROXY', next ? '1' : '0');
        } catch {}
        setProxyOn(next);
    };
    return (
        <div style={{ fontSize: 11, background: '#111', padding: 8, marginTop: 4, borderRadius: 4, wordBreak: 'break-all' }}>
            <div><strong>hostname:</strong> {window.location.hostname}</div>
            <div><strong>ancestorOrigins:</strong> {(() => { try { return Array.from(window.location.ancestorOrigins || []).join(', ') || '(none)'; } catch { return '(unavailable)'; } })()}</div>
            <div><strong>UA:</strong> {navigator.userAgent}</div>
            <div style={{ marginTop: 4 }}>
                <button onClick={toggleProxy}>
                    Twitch source: {proxyOn ? 'PROXY (/.proxy/twitch)' : 'DIRECT (player.twitch.tv)'} — tap to toggle
                </button>
                <div style={{ opacity: 0.7 }}>Reopen the Twitch stream after toggling.</div>
            </div>
            <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                <button onClick={refresh}>Refresh</button>
                <button onClick={copy}>Copy</button>
                <button onClick={clear}>Clear</button>
            </div>
            {logs.map((l, i) => <div key={i} style={{ marginTop: 2 }}>{l}</div>)}
        </div>
    );
}

// Loads several URLs in throwaway hidden iframes and reports whether each fires
// `onload` (frame allowed to render) or times out (blocked — no CSP event on iOS).
// Lets us compare direct vs proxied Twitch against known-good/known-blocked controls.
function IframeLoadTester() {
    const [results, setResults] = useState<string[]>([]);
    const log = (line: string) =>
        setResults((r) => [...r, `${new Date().toISOString().slice(11, 23)} ${line}`]);

    const runTest = () => {
        setResults([]);
        const parents = [window.location.hostname, 'discord.com', 'discordsays.com'];
        const query = `?channel=valorant&${parents.map((p) => `parent=${encodeURIComponent(p)}`).join('&')}&autoplay=false&muted=false`;
        const tests = [
            { name: 'example.com (non-whitelisted control)', src: 'https://example.com/' },
            { name: 'cloudflare (whitelisted control)', src: 'https://customer-c6kmyujkvesrdmhe.cloudflarestream.com/8fb776397ab99e0d4867875f8044d3e8/iframe' },
            { name: 'twitch DIRECT', src: `https://player.twitch.tv/${query}` },
            { name: 'twitch PROXY (/.proxy/twitch)', src: `/.proxy/twitch/${query}` },
        ];

        tests.forEach(({ name, src }) => {
            const iframe = document.createElement('iframe');
            iframe.style.cssText = 'position:absolute;left:-9999px;width:1px;height:1px;border:none;';
            let settled = false;
            const finish = (result: string) => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                log(`${result}: ${name}`);
                try { iframe.remove(); } catch {}
            };
            const timer = setTimeout(() => finish('⏳ TIMEOUT (no onload 8s)'), 8000);
            iframe.onload = () => finish('✅ onload');
            iframe.onerror = () => finish('❌ onerror');
            iframe.src = src;
            document.body.appendChild(iframe);
            log(`… loading ${name}`);
        });
    };

    const copy = () =>
        navigator.clipboard?.writeText(results.join('\n')).catch(() => {});

    return (
        <div style={{ fontSize: 11, background: '#111', padding: 8, marginTop: 4, borderRadius: 4, wordBreak: 'break-all' }}>
            <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={runTest}>Run iframe load test</button>
                <button onClick={copy}>Copy</button>
            </div>
            {results.map((l, i) => <div key={i} style={{ marginTop: 2 }}>{l}</div>)}
        </div>
    );
}

export default function AriseDebugMenu() {
    const dispatch = useAppDispatch();
    const isDebugMenuOpen = useAppSelector((state) => state.app.isDebugMenuOpen);
    const currentUserProfileV2 = useAppSelector((state) => state.profile.currentUserProfileV2);
    const location = useAppSelector((state) => state.app.location);
    const theme = useTheme();
    const [error, setError] = useState<string | null>();
    const enableHiddenDebugMenuAccess = theme.metadata?.customJSON?.informationDrawer?.enableHiddenDebugMenuAccess;

    useEffect(() => {
        if (!enableHiddenDebugMenuAccess) return;
        (window as any).__tbdb = () => dispatch(toggleDebugMenu());
        return () => {
            delete (window as any).__tbdb;
        };
    }, [enableHiddenDebugMenuAccess, dispatch]);

    const [iframeEventName, setIframeEventName] = useState('');
    const [iframeEventPayload, setIframeEventPayload] = useState('');
    const [rewardErrorCode, setRewardErrorCode] = useState('no_codes');

    const handleSimulateIframeEvent = () => {
        setError(null);
        try {
            // Parse payload if it's JSON, otherwise use as string
            let parsedPayload = iframeEventPayload;
            try {
                parsedPayload = JSON.parse(iframeEventPayload);
            } catch {
                // Keep as string if not valid JSON
            }

            console.log('Simulating iFrame message event:', {
                type: iframeEventName,
                payload: parsedPayload,
            });

            // Simulate a message event from an iframe (handled by AriseBridgeHost)
            const messageEvent = new MessageEvent('message', {
                data: {
                    type: iframeEventName,
                    payload: parsedPayload,
                },
                origin: window.location.origin,
            });
            window.dispatchEvent(messageEvent);

            console.log(`MessageEvent "${iframeEventName}" dispatched with payload:`, parsedPayload);
        } catch (err) {
            setError('Failed to simulate event: ' + (err instanceof Error ? err.message : String(err)));
        }
    };

    const handleSimulateIframeEventAfterDelay = () => {
        dispatch(toggleDebugMenu());
        setTimeout(() => {
            handleSimulateIframeEvent();
        }, 2000);
    };

    const handleClearLocalProfile = () => {
        setError(null);
        try {
            clearProfileV2FromLocalStorage();
        } catch (err) {
            setError('Failed to clear local profile');
        }
    };

    const handleClearRemoteProfile = async () => {
        setError(null);
        try {
            await Promise.all([
                arise.resetProfileBadges(),
                arise.resetProfileTitles(),
                arise.resetProfileMissionCompleted(),
            ]);
        } catch (err) {
            setError('Failed to clear remote profile');
        }
    };

    const handleClearProfile = async () => {
        setError(null);
        try {
            clearProfileV2FromLocalStorage();
            await Promise.all([
                arise.resetProfileBadges(),
                arise.resetProfileTitles(),
                arise.resetProfileMissionCompleted(),
            ]);
        } catch (err) {
            setError('Failed to clear profile');
        }
    };

    const handleClearLocalVideoData = async () => {
        setError(null);
        try {
            clearLocalVideoProgressFromStorage();
        } catch (err) {
            setError('Failed to clear local video data');
        }
    };

    const handleClearSavedLang = () => {
        setError(null);
        try {
            clearSavedLangIso();
        } catch (err) {
            setError('Failed to clear saved language');
        }
    };

    const handleClearLocalAuthToken = () => {
        setError(null);
        try {
            window.localStorage.removeItem('ARISEENGINE_AUTHTOKEN');
        } catch (err) {
            setError('Failed to clear auth token');
        }
    };

    const handleClearAllLocalStorage = () => {
        setError(null);
        try {
            window.localStorage.clear();
        } catch (err) {
            setError('Failed to clear local storage');
        }
    };

    const getArisewareData = () => {
        const iframeURL = location?.iframeURL || location?.latestArisewareBuildURL;
        if (!iframeURL) return 'No Ariseware';
        try {
            const arisewareVersionDateTime = iframeURL.match(/\/(\d+)\//)[1];
            const dateVersion = new Date(parseInt(arisewareVersionDateTime)).toLocaleString();
            return `${dateVersion} (${arisewareVersionDateTime})`;
        } catch (error) {
            return iframeURL || 'No Ariseware';
        }
    };

    return (
        <Modal
            title="Debug Menu"
            isVisible={isDebugMenuOpen}
            onClose={() => dispatch(toggleDebugMenu())}
            hasCloseButton={true}
            modalClassname={styles.AriseDebugMenu}
            zIndex={2000}
            canScroll
            leaveSpaceAboveAndBelow={false}
            shouldCloseOnExternalClick={false}
        >
            {error && (
                <div className={styles.errorMessage}>
                    <strong>Error:</strong> {error}
                </div>
            )}
            <section>
                <h3>System Info</h3>
                <ul>
                    <li>
                        <strong>Arise Frontend version: </strong>
                        {appVersion}
                    </li>
                    <li>
                        <strong>Ariseware version:</strong> {getArisewareData()}
                    </li>
                    <li>
                        <strong>User ID:</strong> {currentUserProfileV2?.userId}
                    </li>
                    <li>
                        <strong>Auth token:</strong> {window.localStorage.getItem('ARISEENGINE_AUTHTOKEN') || ''}
                    </li>
                </ul>
            </section>
            <section>
                <h3>Profile Actions</h3>
                <p>Please reload after clearing</p>
                <button onClick={handleClearLocalProfile}>Clear Local Profile</button>
                <button onClick={handleClearRemoteProfile}>Clear Remote Profile</button>
                <button onClick={handleClearProfile}>Clear All Profile Data</button>
            </section>
            <section>
                <h3>Features</h3>
                <p>Please reload after clearing video data</p>
                <button onClick={handleClearLocalVideoData}>Clear Local Video Data</button>
                <button
                    onClick={() => {
                        dispatch(toggleDebugMenu());
                        setTimeout(() => {
                            dispatch(setCurrentlyViewingMedia({ resourceUrl: 'https://www.youtube.com/watch?v=ECI3eCAxRGw', resourceType: 'video' }));
                        }, 300);
                    }}
                >
                    Open YouTube Video
                </button>
                <button
                    onClick={() => {
                        dispatch(toggleDebugMenu());
                        setTimeout(() => {
                            dispatch(setCurrentlyViewingMedia({ resourceUrl: 'https://customer-c6kmyujkvesrdmhe.cloudflarestream.com/8fb776397ab99e0d4867875f8044d3e8/iframe', resourceType: 'video' }));
                        }, 300);
                    }}
                >
                    Open Cloudflare Video
                </button>
                <button
                    onClick={() => {
                        dispatch(toggleDebugMenu());
                        setTimeout(() => {
                            dispatch(setCurrentlyViewingMedia({ resourceUrl: 'https://www.twitch.tv/valorant', resourceType: 'video' }));
                        }, 300);
                    }}
                >
                    Open Twitch Stream
                </button>
                <TwitchDebugPanel />
                <IframeLoadTester />
                <button
                    onClick={() => {
                        dispatch(toggleDebugMenu());
                        setTimeout(() => {
                            dispatch(
                                setCurrentlyViewingReward({
                                    id: '123',
                                    createdAt: '2026-05-18T08:16:50.693Z',
                                    universeId: '456',
                                    modalTitle: 'Congrats!',
                                    name: 'Prize',
                                    description: 'Ut enim ad minim veniam, quis nostrud exercitation ullamco.',
                                    mainImagePath: 'uploads/97cab966-aa05-4e90-8b0b-f1448c318de5/16x9.webp',
                                    secondaryImagePath: 'uploads/97cab966-aa05-4e90-8b0b-f1448c318de5/16x9.webp',
                                    redemptionInstructions: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
                                    sfxUrl: null,
                                    externalUrl: 'https://thosebeyond.io',
                                    readMoreUrl: 'https://thosebeyond.io',
                                    readMoreText: 'Read more',
                                    isFiniteSupply: true,
                                    oneCodeForAll: false,
                                    messageText: "Here's your code!",
                                    redemptionType: 'code',
                                    redemptionValue: 'code-1234',
                                    ctaText: 'CTA',
                                    ctaMethod: 'open_link',
                                }),
                            );
                        }, 2000);
                    }}
                >
                    View Code Reward Modal
                </button>
                <select value={rewardErrorCode} onChange={(e) => setRewardErrorCode(e.target.value)} style={{ color: 'black', marginBottom: 4 }}>
                    <option value="lose">lose</option>
                    <option value="no_codes">no_codes</option>
                    <option value="cooldown">cooldown</option>
                    <option value="inactive">inactive</option>
                    <option value="in_progress">in_progress</option>
                    <option value="GENERIC_ERROR">GENERIC_ERROR</option>
                </select>
                <button
                    onClick={() => {
                        dispatch(toggleDebugMenu());
                        setTimeout(() => {
                            dispatch(setRewardClaimError({ code: rewardErrorCode }));
                        }, 300);
                    }}
                >
                    View Error Reward Modal
                </button>
            </section>
            {process.env.PUBLIC_SUPPORT_TRANSLATIONS === 'true' && <LanguageCycler />}
            <section>
                <h3>User</h3>
                <p>Please reload after clearing</p>
                <button onClick={handleClearLocalAuthToken}>Clear Local Auth Token</button>
                <button onClick={handleClearSavedLang}>Clear Saved Language</button>
                <button onClick={handleClearAllLocalStorage}>Clear All Local Storage Data</button>
            </section>
            <section>
                <h3>Simulate event from iFrame</h3>
                <div className={styles.formGroup}>
                    <label>Event name:</label>
                    <input
                        type="text"
                        placeholder="e.g. open-url"
                        value={iframeEventName}
                        onChange={(e) => setIframeEventName(e.target.value)}
                    />
                </div>
                <div className={styles.formGroup}>
                    <label>Payload detail (JSON or string):</label>
                    <textarea
                        placeholder="e.g. https://www.google.com"
                        value={iframeEventPayload}
                        onChange={(e) => setIframeEventPayload(e.target.value)}
                        rows={3}
                    />
                </div>
                <button onClick={handleSimulateIframeEvent}>Send</button>
                <button onClick={handleSimulateIframeEventAfterDelay}>Close Debug, wait 5 seconds and then Send</button>
            </section>
        </Modal>
    );
}
